/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  inject,
  Input,
  OnInit,
  EventEmitter,
  Output,
} from '@angular/core';
import MapView from '@arcgis/core/views/MapView';
import Viewpoint from '@arcgis/core/Viewpoint';
import { FormsModule } from '@angular/forms';
import { ArcgisService } from '../../../services/arcgis/arcgis.service';
import { Bookmark } from '../../../models/site-bookmark';
import { ArcGISItem, ArcGISItemType } from '../../../models/arcgis-item';
import { DestroyBase } from '../../../../../shared/base-classes/destroy-component-base';
import { takeUntil } from 'rxjs';
import Graphic from '@arcgis/core/Graphic';
import { AuditLogService } from '../../../../../shared/services/audit-log/audit-log.service';

@Component({
  selector: 'app-site-toolkit-bookmarks',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './site-toolkit-bookmarks.component.html',
  styleUrl: './site-toolkit-bookmarks.component.scss',
})
export class SiteToolkitBookmarksComponent
  extends DestroyBase
  implements OnInit
{
  @Input({ required: true }) mapView!: MapView;
  @Output() filterStateChange = new EventEmitter<any[]>();

  bookmarks: Bookmark[] = [];
  showDialog = false;
  bookmarkName = '';
  currentPage = 1;
  pageSize = 50;

  arcgisService = inject(ArcgisService);
  auditLogService = inject(AuditLogService);

  ngOnInit() {
    this.loadBookmarks();
  }

  private loadBookmarks() {
    this.arcgisService
      .getArcGISItems(ArcGISItemType.BOOKMARK, this.currentPage, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res.results.length > 0) {
          const bookmarkItem = res.results[0];
          const bookmarkData = bookmarkItem.data as { bookmarks: Bookmark[] };
          this.bookmarks = bookmarkData.bookmarks.map((bookmark) => ({
            ...bookmark,
            arcgis_item_id: bookmarkItem.id,
            viewpoint: Viewpoint.fromJSON(bookmark.viewpoint),
          }));
        }
      });
  }

  private saveToStorage(bookmark: Bookmark) {
    const bookmarksToSave = [...this.bookmarks, bookmark].map((b) => ({
      ...b,
      viewpoint: b.viewpoint?.toJSON() || b.viewpoint,
    }));

    const item: ArcGISItem = {
      type: ArcGISItemType.BOOKMARK,
      data: { bookmarks: bookmarksToSave },
      user_id: 0,
      updated_on_utc: new Date().toISOString(),
      created_on_utc: new Date().toISOString(),
    };

    this.arcgisService
      .saveArcGISItem(item)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.bookmarks = bookmarksToSave.map((b) => ({
          ...b,
          viewpoint:
            b.viewpoint instanceof Viewpoint
              ? b.viewpoint
              : Viewpoint.fromJSON(b.viewpoint),
        }));
      });
  }

  async addBookmark() {
    this.bookmarkName = `Bookmark ${this.bookmarks.length + 1}`;
    this.showDialog = true;
  }

  private async compressImage(
    dataUrl: string,
    maxWidth = 300,
  ): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = dataUrl;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ratio = maxWidth / img.width;
        canvas.width = maxWidth;
        canvas.height = img.height * ratio;

        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(dataUrl);

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
    });
  }

  async saveBookmark() {
    if (this.bookmarkName) {
      this.auditLogService.logFeature({
        featureName: 'site_bookmarks',
        action: 'save',
        meta: { bookmarkName: this.bookmarkName },
        description: 'User saves a site bookmark',
      });

      const screenshot = await this.mapView.takeScreenshot();
      const compressedThumbnail = await this.compressImage(screenshot.dataUrl);

      // Capture layer states
      const layerStates = this.mapView.map.layers.toArray().map((layer) => ({
        id: layer.id,
        visible: layer.visible,
        definitionExpression:
          'definitionExpression' in layer
            ? (layer as any).definitionExpression
            : undefined,
      }));

      // Get all graphics from the map view's graphics layer
      const graphics = this.mapView.graphics
        .toArray()
        .map((graphic) => graphic.toJSON());

      // Get filter states from the parent component
      const filterStates = (this.mapView as any).filterConfigs?.map(
        (filter: any) => ({
          id: filter.id,
          selectedValue: filter.selectedValue,
        }),
      );

      const bookmark: Bookmark = {
        id: Date.now().toString(),
        name: this.bookmarkName,
        viewpoint: this.mapView.viewpoint.clone(),
        thumbnail: compressedThumbnail,
        graphics: graphics,
        layerStates: layerStates,
        filterStates: filterStates,
      };
      this.saveToStorage(bookmark);
      this.closeDialog();
    }
  }

  deleteBookmark(id: string, event: Event) {
    event.stopPropagation();

    const updatedBookmarks = this.bookmarks
      .filter((b) => b.id !== id)
      .map((b) => ({
        ...b,
        viewpoint: b.viewpoint.toJSON(),
      }));

    const item: ArcGISItem = {
      type: ArcGISItemType.BOOKMARK,
      data: { bookmarks: updatedBookmarks },
      user_id: 0,
      updated_on_utc: new Date().toISOString(),
      created_on_utc: new Date().toISOString(),
    };

    this.arcgisService
      .saveArcGISItem(item)
      .pipe(takeUntil(this.destroy$))
      .subscribe((savedItem) => {
        this.bookmarks = (
          savedItem.data as { bookmarks: Bookmark[] }
        ).bookmarks.map((b) => ({
          ...b,
          arcgis_item_id: savedItem.id,
          viewpoint: Viewpoint.fromJSON(b.viewpoint),
        }));
      });
  }

  closeDialog() {
    this.showDialog = false;
    this.bookmarkName = '';
  }

  goToBookmark(bookmark: Bookmark) {
    // Clear existing graphics
    this.mapView.graphics.removeAll();

    // Restore graphics if they exist
    if (bookmark.graphics) {
      const graphics = bookmark.graphics.map((g) => Graphic.fromJSON(g));
      this.mapView.graphics.addMany(graphics);
    }

    // Restore layer states
    if (bookmark.layerStates) {
      bookmark.layerStates.forEach((state) => {
        const layer = this.mapView.map.layers.find((l) => l.id === state.id);
        if (layer) {
          layer.visible = state.visible;
          if (
            'definitionExpression' in layer &&
            state.definitionExpression !== undefined
          ) {
            (layer as any).definitionExpression = state.definitionExpression;
          }
        }
      });
    }

    // Restore filter states - emit event to parent component
    if (bookmark.filterStates) {
      // Assuming you have an EventEmitter for this
      this.filterStateChange.emit(bookmark.filterStates);
    }

    // Go to the saved viewpoint
    this.mapView.goTo(bookmark.viewpoint);
  }
}
